import * as Sentry from '@sentry/react';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN || '';

export function initSentry() {
  if (!SENTRY_DSN) {
    console.log('[Sentry] DSN not configured, skipping initialization');
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE,

    // Performance monitoring - sample 10% of transactions in production
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,

    // Session replay - capture 10% of sessions, 100% on error
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    // Only send errors in production, not development
    enabled: import.meta.env.PROD,

    // Integrations
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],

    // Filter out noisy errors
    ignoreErrors: [
      // Browser extensions
      'chrome-extension',
      'moz-extension',
      // Network errors that are expected
      'Network request failed',
      'Load failed',
      'Failed to fetch',
      // Service worker errors (common on iOS Safari during navigation/background)
      /Script.*sw\.js.*load failed/i,
      /Service Worker/i,
      'ServiceWorker',
      // User-initiated aborts
      'AbortError',
      'The user aborted a request',
      // iOS Safari specific transient errors
      "The operation couldn't be completed",
      'cancelled',
    ],

    // Filter out sensitive data
    beforeSend(event) {
      // Remove auth tokens from breadcrumbs
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map((breadcrumb) => {
          if (breadcrumb.data?.url) {
            // Remove any auth tokens from URLs
            breadcrumb.data.url = breadcrumb.data.url.replace(
              /token=[^&]+/g,
              'token=[FILTERED]'
            );
          }
          return breadcrumb;
        });
      }
      return event;
    },
  });

  console.log(`[Sentry] Initialized for environment: ${import.meta.env.MODE}`);
}

// Export Sentry for manual error capture
export { Sentry };

// Helper to set user context after login
export function setSentryUser(user: { id: string; email?: string; walletAddress?: string }) {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.walletAddress,
  });
}

// Helper to clear user context on logout
export function clearSentryUser() {
  Sentry.setUser(null);
}
